import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { PaymentsRange } from "views/Dashboard/components/PaymentsRangeFilter";

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar day written YYYY-MM-DD ("2026-02-31" is not one). */
const isDay = (value: string | null): value is string => {
  if (!value || !DAY.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
};

/**
 * The period kept in the URL (?from=YYYY-MM-DD&to=YYYY-MM-DD), next to the other
 * parameters, so a shared link shows the same numbers. Explicit dates, never a
 * preset name: "previous month" would mean another month next month.
 *
 * Both dates, in order, or none: anything else in the URL is ignored and the
 * page shows all time, instead of the API answering a 400.
 */
const useRangeParams = (): [PaymentsRange, (range: PaymentsRange) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const from = searchParams.get("from");
  const to = searchParams.get("to");

  const range = useMemo<PaymentsRange>(
    () => (isDay(from) && isDay(to) && from <= to ? { from, to } : {}),
    [from, to],
  );

  const setRange = useCallback(
    (next: PaymentsRange) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next.from && next.to) {
            params.set("from", next.from);
            params.set("to", next.to);
          } else {
            params.delete("from");
            params.delete("to");
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return [range, setRange];
};

export default useRangeParams;
